import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Play, Pause, ChevronLeft, ChevronRight, RotateCcw,
  Code2, Copy, Check, Loader2, AlertCircle, Link2, AlertTriangle
} from 'lucide-react';
import SequenceDiagram from '../../components/SequenceDiagram';
import { FLOW_SCENARIOS, FLOW_ACTORS, SCENARIO_ORDER, SCENARIO_GROUPS } from '../../constants/flowScenarios';
import { messageApi } from '../../services/iso20022Api';

const KIND_BADGE = {
  req:  'bg-blue-100 text-blue-800',
  ok:   'bg-green-100 text-green-800',
  warn: 'bg-amber-100 text-amber-800',
  rjct: 'bg-red-100 text-red-800',
};

const STEP_MS = 1600;

export default function FlowSimulator() {
  const [scenarioId, setScenarioId] = useState(SCENARIO_ORDER[0]);
  const [stepIndex, setStepIndex]   = useState(0);
  const [playing, setPlaying]       = useState(false);
  const [xmlCache, setXmlCache]     = useState({}); // sourceStepIndex -> xml
  const [xmlState, setXmlState]     = useState({ loading: false, error: null });
  const [showXml, setShowXml]       = useState(false);
  const [copied, setCopied]         = useState(false);
  const timerRef = useRef(null);

  const scenario = FLOW_SCENARIOS[scenarioId];
  const actors = scenario.actors || FLOW_ACTORS;
  const step = scenario.steps[stepIndex];
  const sourceIdx = step.sameAs ?? stepIndex;

  // Fetch the real engine XML for the current step's source message.
  const fetchXml = useCallback(async (scn, srcIdx) => {
    if (xmlCache[`${scn.id}:${srcIdx}`] !== undefined) return;
    const src = scn.steps[srcIdx];
    if (!src?.payload) return;
    setXmlState({ loading: true, error: null });
    try {
      const xml = await messageApi.generateMessage(src.msgType, src.payload, 'xml', true);
      setXmlCache(prev => ({ ...prev, [`${scn.id}:${srcIdx}`]: typeof xml === 'string' ? xml : JSON.stringify(xml, null, 2) }));
      setXmlState({ loading: false, error: null });
    } catch (e) {
      setXmlState({ loading: false, error: e.response?.data?.message || e.message || 'Generation failed' });
    }
  }, [xmlCache]);

  useEffect(() => { fetchXml(scenario, sourceIdx); }, [scenario, sourceIdx, fetchXml]);

  // Playback timer
  useEffect(() => {
    if (!playing) return;
    timerRef.current = setInterval(() => {
      setStepIndex(prev => {
        if (prev >= scenario.steps.length - 1) { setPlaying(false); return prev; }
        return prev + 1;
      });
    }, STEP_MS);
    return () => clearInterval(timerRef.current);
  }, [playing, scenario.steps.length]);

  const switchScenario = (id) => {
    setScenarioId(id); setStepIndex(0); setPlaying(false); setShowXml(false);
  };
  const goTo = (i) => { setPlaying(false); setStepIndex(Math.max(0, Math.min(i, scenario.steps.length - 1))); };
  const togglePlay = () => {
    if (!playing && stepIndex >= scenario.steps.length - 1) setStepIndex(0);
    setPlaying(p => !p);
  };

  const currentXml = xmlCache[`${scenario.id}:${sourceIdx}`];
  const copyXml = () => {
    if (!currentXml) return;
    navigator.clipboard.writeText(currentXml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Flow Simulator</h1>
        <p className="text-sm text-gray-500 mt-1">
          Step through an ISO 20022 payment flow. Each message is generated live by the engine —
          watch how one <span className="font-mono text-xs">EndToEndId</span> threads through them all.
        </p>
      </div>

      {/* Scenario picker — grouped by flow family */}
      <div className="space-y-3">
        {SCENARIO_GROUPS.map(group => (
          <div key={group}>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">{group}</p>
            <div className="flex flex-wrap gap-2">
              {SCENARIO_ORDER.filter(id => FLOW_SCENARIOS[id].category === group).map(id => (
                <button
                  key={id}
                  onClick={() => switchScenario(id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    id === scenarioId
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {FLOW_SCENARIOS[id].label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Diagram */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
        <SequenceDiagram
          actors={scenario.actors || FLOW_ACTORS}
          steps={scenario.steps}
          activeIndex={stepIndex}
          onStepClick={goTo}
        />
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        <button onClick={() => goTo(stepIndex - 1)} disabled={stepIndex === 0}
          className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={togglePlay}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          {playing ? 'Pause' : 'Play flow'}
        </button>
        <button onClick={() => goTo(stepIndex + 1)} disabled={stepIndex === scenario.steps.length - 1}
          className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40">
          <ChevronRight className="w-5 h-5" />
        </button>
        <button onClick={() => goTo(0)}
          className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50" aria-label="Reset">
          <RotateCcw className="w-5 h-5" />
        </button>
        <span className="text-sm text-gray-500 min-w-[44px] text-right">
          {stepIndex + 1} / {scenario.steps.length}
        </span>
      </div>

      {/* Detail panel */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-3 mb-2">
          <span className={`font-mono text-sm px-2.5 py-1 rounded-md ${KIND_BADGE[step.kind] || KIND_BADGE.req}`}>
            {step.msgType}
          </span>
          <span className="text-sm text-gray-500">
            {actors[step.from]} → {actors[step.to]}
          </span>
        </div>
        <p className="text-base font-semibold text-gray-900 mb-1">{step.title}</p>
        <p className="text-sm text-gray-600 leading-relaxed mb-3">{step.description}</p>

        <div className="flex items-center gap-2 flex-wrap">
          <Link2 className="w-3.5 h-3.5 text-gray-400" />
          <span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-md">
            {scenario.correlationId}
          </span>
          <span className="text-xs text-gray-400">· {step.corrNote}</span>
        </div>

        {/* XML toggle */}
        <button onClick={() => setShowXml(s => !s)}
          className="mt-4 flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700">
          <Code2 className="w-4 h-4" />
          {showXml ? 'Hide' : 'View'} generated XML
          {step.sameAs != null && <span className="text-xs text-gray-400">(relayed — same message)</span>}
        </button>

        {showXml && (
          <div className="mt-3">
            {xmlState.loading && (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                <Loader2 className="w-4 h-4 animate-spin" /> Generating from engine…
              </div>
            )}
            {xmlState.error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                <p className="text-xs text-red-700">{xmlState.error}</p>
              </div>
            )}
            {!xmlState.loading && !xmlState.error && currentXml && (
              <div className="relative">
                <button onClick={copyXml}
                  className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-white border border-gray-200 text-gray-600 hover:bg-gray-50">
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-all max-h-96 overflow-y-auto">
                  {currentXml}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fault / error injection reference */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" /> Fault / error injection
        </h2>
        <p className="text-xs text-gray-500 mt-1 mb-3 max-w-2xl">
          Make the Sandbox switch misbehave on demand to test your gateway's error handling.
          Set the <span className="font-mono">X-Cert-Scenario</span> request header, or embed a magic
          value in the message. Injected faults are <span className="font-medium">excluded from certification
          scoring</span> — they never pass or fail a case — but still appear in your Message Log.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1.5">Header — X-Cert-Scenario</p>
            <div className="space-y-1 text-xs">
              {[
                ['REJECT:AC01', 'signed RJCT with reason code AC01'],
                ['TIMEOUT:15', 'hold the response 15s (max 30) then reply late'],
                ['MALFORMED', 'return a truncated, unparseable body'],
                ['ACCEPT', 'force the happy path'],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <span className="font-mono bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded shrink-0">{k}</span>
                  <span className="text-gray-600">{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1.5">Magic values in the message</p>
            <div className="space-y-1 text-xs">
              {[
                ['pacs.008 amount 9999.13', 'REJECT AG01'],
                ['pacs.008 amount 9999.77', 'TIMEOUT (15s)'],
                ['pacs.008 amount 9999.99', 'MALFORMED response'],
                ['creditor / lookup account ends 0000', 'reject / not verified'],
                ['acmt.007 MsgId contains REJECT', 'registration rejected'],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <span className="font-mono bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded shrink-0">{k}</span>
                  <span className="text-gray-600">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
